import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../config";
import { Bell, X } from "lucide-react";

/**
 * One-click "Enable mobile notifications" prompt.
 *
 * Appears on the admin panel and user dashboard whenever:
 *  - the browser supports Notifications + Service Worker + Push,
 *  - permission state is `default` (not yet granted/denied),
 *  - and the user hasn't dismissed the prompt this session.
 *
 * Tapping the button asks for permission, registers the subscription
 * with the backend, and disappears. Works inside a PWA installed to the
 * iOS home screen (Safari 16.4+) and on every Android Chrome variant.
 *
 * Why not always-on bell icon? The "in-your-face but dismissible"
 * pattern is what messenger apps use and the conversion data on Web
 * Push opt-in flows is +35-60% vs gear-buried settings toggles.
 */

const VAPID_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;
const HIDDEN_PREFIXES = ["/auth", "/legal", "/preview", "/lifetime", "/affiliate-kit"];

function base64ToUint8(b64) {
  const padding = "=".repeat((4 - b64.length % 4) % 4);
  const s = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(s);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

export default function PushPermissionPrompt() {
  const [visible, setVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Only show on /dashboard, /admin, or /results — the high-intent
    // surfaces. Everywhere else this would be noise.
    const path = window.location.pathname;
    if (HIDDEN_PREFIXES.some((p) => path.startsWith(p))) return;
    const onIntent = path.startsWith("/dashboard")
      || path.startsWith("/admin")
      || path.startsWith("/results");
    if (typeof window === "undefined") return;
    if (!("serviceWorker" in navigator)) return;
    if (!("PushManager" in window)) return;
    if (!("Notification" in window)) return;
    if (Notification.permission !== "default") return;
    if (!VAPID_KEY) return;
    try {
      if (sessionStorage.getItem("trp_push_prompt_dismissed") === "1") return;
    } catch { /* ignore */ }

    // Two ways to surface the prompt:
    //   1. Path-based timer  — user already on /dashboard|/admin|/results,
    //      4s delay so the page can settle.
    //   2. Custom event hook — `trp:scan-completed` fires the moment a
    //      scan resolves. We want a much shorter 2s delay there because
    //      the user's intent is at its peak right after seeing results.
    let cleanup = () => {};
    if (onIntent) {
      const t = setTimeout(() => setVisible(true), 4000);
      cleanup = () => clearTimeout(t);
    }
    const onScan = () => {
      // Defer 2s so the celebratory score animation has time to play.
      setTimeout(() => {
        if (Notification.permission !== "default") return;
        try {
          if (sessionStorage.getItem("trp_push_prompt_dismissed") === "1") return;
        } catch { /* ignore */ }
        setVisible(true);
      }, 2000);
    };
    window.addEventListener("trp:scan-completed", onScan);
    return () => {
      cleanup();
      window.removeEventListener("trp:scan-completed", onScan);
    };
  }, []);

  if (!visible) return null;

  const handleEnable = async () => {
    setSubmitting(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        setSubmitting(false);
        setVisible(false);
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const existing = await reg.pushManager.getSubscription();
      const sub = existing || await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: base64ToUint8(VAPID_KEY),
      });
      const subJson = sub.toJSON();
      await axios.post(`${API}/user-notifications/subscribe`, {
        endpoint: subJson.endpoint,
        keys: subJson.keys,
      }, { withCredentials: true });
      setVisible(false);
    } catch (e) {
      console.warn("push subscribe failed", e);
    }
    setSubmitting(false);
  };

  const handleDismiss = () => {
    try { sessionStorage.setItem("trp_push_prompt_dismissed", "1"); } catch { /* ignore */ }
    setVisible(false);
  };

  return (
    <div
      data-testid="push-permission-prompt"
      className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[55] w-[calc(100%-1.5rem)] max-w-md bg-white text-gray-900 rounded-2xl shadow-2xl border border-gray-200 p-4 flex items-start gap-3 animate-in fade-in slide-in-from-bottom-4 duration-300"
      role="dialog"
      aria-live="polite"
    >
      <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
        <Bell className="w-5 h-5 text-amber-600" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-bold text-sm text-gray-900">
          Zapni notifikácie na telefón
        </p>
        <p className="text-xs text-gray-600 mt-0.5 leading-snug">
          Dostaneš upozornenie keď AI nájde citáciu, klesne skóre, alebo pribudne nový lead.
        </p>
        <div className="flex items-center gap-2 mt-3">
          <button
            type="button"
            onClick={handleEnable}
            disabled={submitting}
            data-testid="push-prompt-enable"
            className="px-3 py-1.5 rounded-lg bg-gray-900 text-white text-xs font-bold hover:bg-gray-800 disabled:opacity-60"
          >
            {submitting ? "Zapínam…" : "Zapnúť"}
          </button>
          <button
            type="button"
            onClick={handleDismiss}
            data-testid="push-prompt-dismiss"
            className="px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-500 hover:text-gray-900"
          >
            Neskôr
          </button>
        </div>
      </div>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Zatvoriť"
        className="text-gray-400 hover:text-gray-700 flex-shrink-0"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
